import { CustomListKey } from "@sp-crm/core";
import React from "react";
import { useCustomList } from "store/selectors/bridge";
import {
    findInactiveClientDays,
    getWorkflowStatusIds,
    hasCheckLaterTrigger,
    hasClientStatusTrigger,
    hasEmailOpenedTrigger,
    hasInactiveClientTrigger,
    hasMoveInTrigger,
    hasReferralSourceTrigger,
} from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";

interface WorkflowTriggerSummaryProps {
    workflow: EditableWorkflow;
}

const StatusTagList: React.FC<{ statusIds: string[] }> = ({ statusIds }) => {
    const { getListItemName, status } = useCustomList(CustomListKey.ClientStatus);

    if (status !== "ready") {
        return <span>...</span>;
    }

    const names = statusIds.map(id => getListItemName(id)).filter(Boolean);

    if (names.length === 0) {
        return <span className="text-gray-500 italic">any status</span>;
    }

    return (
        <span className="inline-flex flex-wrap gap-1">
            {names.map((name, i) => (
                <span
                    key={i}
                    className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                    {name}
                </span>
            ))}
        </span>
    );
};

const ClientStatusTriggerSummary: React.FC = () => {
    return <span>Runs when client status changes</span>;
};

const EmailOpenedTriggerSummary: React.FC<{ workflow: EditableWorkflow }> = ({
    workflow,
}) => {
    const thresholdDays = workflow.trigger?.emailOpened?.activityThresholdDays || 0;

    return (
        <span>
            Runs when a client email is opened
            {thresholdDays > 0 && <> after {thresholdDays} days of inactivity</>}
        </span>
    );
};

const InactiveClientTriggerSummary: React.FC<{ workflow: EditableWorkflow }> = ({
    workflow,
}) => {
    const days = findInactiveClientDays(workflow);

    return (
        <span>
            Runs daily for active clients who have had no activity for {days}{" "}
            {days === 1 ? "day" : "days"}
        </span>
    );
};

const ReferralSourceTriggerSummary: React.FC = () => {
    return <span>Runs when a referral source is set</span>;
};

const MoveInTriggerSummary: React.FC = () => {
    return <span>Runs when the move date is set</span>;
};

const CheckLaterTriggerSummary: React.FC = () => {
    return <span>Runs when a check back later date is set</span>;
};

export const WorkflowTriggerSummary: React.FC<WorkflowTriggerSummaryProps> = ({
    workflow,
}) => {
    if (hasReferralSourceTrigger(workflow)) {
        return <ReferralSourceTriggerSummary />;
    }

    if (hasMoveInTrigger(workflow)) {
        return <MoveInTriggerSummary />;
    }

    if (hasCheckLaterTrigger(workflow)) {
        return <CheckLaterTriggerSummary />;
    }

    if (hasClientStatusTrigger(workflow)) {
        return <ClientStatusTriggerSummary />;
    }

    if (hasEmailOpenedTrigger(workflow)) {
        return <EmailOpenedTriggerSummary workflow={workflow} />;
    }

    if (hasInactiveClientTrigger(workflow)) {
        return <InactiveClientTriggerSummary workflow={workflow} />;
    }

    return <span>Custom</span>;
};

export const WorkflowClientStatusesSummary: React.FC<WorkflowTriggerSummaryProps> = ({
    workflow,
}) => {
    const statusIds = getWorkflowStatusIds(workflow);

    if (statusIds.length === 0) {
        return null;
    }

    return <StatusTagList statusIds={statusIds} />;
};
